<template>
  <div>
    <alarm-breaking-news ref="alarmBreakingNews"/>
    <app-content>
      <dashboard-header
        class="dashboard-header w-100"
        ref="dashboardHeader"
        :title="$t('dashboard.system-information')"
        :modal-title="$t('dashboard.system-information')"
        :items="items"
        @refreshData="onClickSearchButtonHandler"
        @afterLoadAllFilters="onLoadAllFilters"
      />

      <b-tabs
        v-model="activeTab"
        class="w-100"
        @input="refreshData"
      >
        <b-tab
          class="dashboard-grid"
          :title="$t('entities.assets')"
        >
          <div
            v-if="isActiveTabAssets"
            class="dashboard-grid__overview"
          >
            <dashboard-overview
              ref="dashboardOverview"
              :filters="getFilters()"
            />
          </div>
          <div
            class="dashboard-grid__alarms-most-acted-period"
          >
            <most-acted-alarms
              ref="mostActedAlarms"
              :filters="getFilters()"
            />
          </div>

          <div
            class="dashboard-grid__acted-alarms-vs-recognized-alarms"
          >
            <acted-alarms-vs-recognized-alarms-chart
              ref="actedAlarmsVsRecognizedAlarmsChart"
              :filters="getFilters()"
            />
          </div>
        </b-tab>
        <b-tab
          v-if="canViewMap"
          :title="$t('monitoring.map.title')"
        >
          <monitoring-map
            v-if="isActiveTabMap"
            ref="monitoringMap"
            :filters="getFilters()"
          />
        </b-tab>
      </b-tabs>
    </app-content>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AlarmBreakingNews
  from '@/domain/components/generalPanel/dashboard/breakingNews/AlarmBreakingNews';
import DashboardOverview from '@/domain/components/generalPanel/dashboard/DashboardOverview';
import ActedAlarmsVsRecognizedAlarmsChart
  from '@/domain/components/generalPanel/dashboard/ActedAlarmsVsRecognizedAlarmsChart';
import DashboardHeader from '@/components/header/DashboardHeader';
import MostActedAlarms from '@/domain/components/generalPanel/dashboard/MostActedAlarms';

const MonitoringMap = () => import(/* webpackChunkName: "monitoringMap" */
  '@/domain/components/generalPanel/monitoring/MonitoringMap'
  );

const TAB_ASSETS = 0;
const TAB_MAP = 1;

export default {
  components: {
    AlarmBreakingNews,
    DashboardHeader,
    DashboardOverview,
    MostActedAlarms,
    ActedAlarmsVsRecognizedAlarmsChart,
    MonitoringMap,
  },
  data() {
    return {
      activeTab: TAB_ASSETS,
      filters: {
        companiesNames: [],
        companiesIds: [],
        installationsNames: [],
        installationsIds: [],
        regionalsNames: [],
        regionalsIds: [],
      },
      allFilters: {
        companiesNames: [],
        companiesIds: [],
        installationsNames: [],
        installationsIds: [],
        regionalsNames: [],
        regionalsIds: [],
      },
      offlineAlertWasShown: false,
    };
  },
  created() {
    window.addEventListener('online', this.onBrowserOnline);
  },
  beforeDestroy() {
    window.removeEventListener('online', this.onBrowserOnline);
  },
  computed: {
    ...mapGetters([
      'user',
      'isOffLineMode',
    ]),
    isActiveTabAssets() {
      return this.activeTab === TAB_ASSETS;
    },
    isActiveTabMap() {
      return this.activeTab === TAB_MAP;
    },
    canViewMap() {
      return !this.isOffLineMode && this.$hasUserPermission('PER007');
    },
    items() {
      return [
        {
          title: this.$t('header.company'),
          data: this.getCompaniesIfIsNotAllSelected(),
        },
        {
          title: this.$t('header.regional'),
          data: this.getRegionalsIfIsNotAllSelected(),
        },
        {
          title: this.$t('header.installation'),
          data: this.getInstallationsIfIsNotAllSelected(),
        },
      ];
    },
  },
  methods: {
    getCompaniesIfIsNotAllSelected() {
      if (!this.filters?.companiesIds.length) {
        return [];
      }
      const selectedListIds = this.filters.companiesIds;
      const completeListIds = this.allFilters.companiesIds;

      if (completeListIds.every(item => selectedListIds.includes(item))) {
        return [];
      }

      return this.filters.companiesNames?.map(name => ({ name }));
    },
    getRegionalsIfIsNotAllSelected() {
      if (!this.filters?.regionalsIds.length) {
        return [];
      }
      const selectedListIds = this.filters.regionalsIds;
      const completeListIds = this.allFilters.regionalsIds;

      if (completeListIds.every(item => selectedListIds.includes(item))) {
        return [];
      }

      return this.filters.regionalsNames?.map(name => ({ name }));
    },
    getInstallationsIfIsNotAllSelected() {
      if (!this.filters?.installationsIds.length) {
        return [];
      }
      const selectedListIds = this.filters.installationsIds;
      const completeListIds = this.allFilters.installationsIds;

      if (completeListIds.every(item => selectedListIds.includes(item))) {
        return [];
      }

      return this.filters.installationsNames?.map(name => ({ name }));
    },
    onLoadAllFilters(allFilters) {
      this.allFilters.companiesNames = allFilters.companiesNames || [];
      this.allFilters.companiesIds = allFilters.companiesIds || [];
      this.allFilters.installationsNames = allFilters.installationsNames || [];
      this.allFilters.installationsIds = allFilters.installationsIds || [];
      this.allFilters.regionalsNames = allFilters.regionalsNames || [];
      this.allFilters.regionalsIds = allFilters.regionalsIds || [];
    },
    onBrowserOnline() {
      this.offlineAlertWasShown = false;
    },
    isBrowserOffline() {
      return !window.navigator?.onLine;
    },
    showOfflineConnectionAlert() {
      if (this.offlineAlertWasShown) {
        return;
      }

      this.$pushAlert({ code: 'MA122' });
      this.offlineAlertWasShown = true;
    },
    shouldInterruptRefreshBecauseBrowserIsOffline() {
      if (!this.isBrowserOffline()) {
        return false;
      }

      this.showOfflineConnectionAlert();
      return true;
    },
    onClickSearchButtonHandler(filters) {
      if (this.shouldInterruptRefreshBecauseBrowserIsOffline()) {
        return;
      }

      this.filters.companiesNames = filters.companiesNames || [];
      this.filters.companiesIds = filters.companiesIds || [];
      this.filters.installationsNames = filters.installationsNames || [];
      this.filters.installationsIds = filters.installationsIds || [];
      this.filters.regionalsNames = filters.regionalsNames || [];
      this.filters.regionalsIds = filters.regionalsIds || [];

      this.$nextTick(() => {
        if (this.$refs.actedAlarmsVsRecognizedAlarmsChart) {
          this.$refs.actedAlarmsVsRecognizedAlarmsChart.loadChartData();
        }
        if (this.$refs.alarmBreakingNews) {
          this.$refs.alarmBreakingNews.refreshData();
        }
        if (this.$refs.dashboardOverview) {
          this.$refs.dashboardOverview.loadData();
        }
        if (this.$refs.mostActedAlarms) {
          this.$refs.mostActedAlarms.loadListData();
        }
      });
    },
    refreshData() {
      if (this.shouldInterruptRefreshBecauseBrowserIsOffline()) {
        return;
      }

      this.$refs.dashboardHeader.resetCounting();

      if (this.isActiveTabMap && this.canViewMap && this.$refs.monitoringMap) {
        this.$refs.monitoringMap.getInstallations();
      }

      if (this.isActiveTabAssets) {
        this.onClickSearchButtonHandler(this.filters);
      }
    },
    getFilters() {
      return {
        companiesIds: this.filters.companiesIds,
        installationsIds: this.filters.installationsIds,
        regionalsIds: this.filters.regionalsIds,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"
.app-content
  overflow: hidden

.dashboard-grid
  display: grid
  grid-template-areas: "overview overview overview" "acted-table acted-table acted-vs-recognized"
  grid-template-columns: repeat(3, minmax(100px, 1fr))
  column-gap: 1.7rem
  +media-max-md()
    grid-template-areas: "overview" "acted-table" "acted-vs-recognized"
    grid-template-rows: [row1] auto [row2] auto
    grid-template-columns: repeat(1, minmax(100px, 1fr))

  &__overview
    grid-area: overview
    margin-bottom: 1.2rem

  &__alarms-most-acted-period
    grid-area: acted-table
    align-items: stretch

  &__acted-alarms-vs-recognized-alarms
    grid-area: acted-vs-recognized

::v-deep
  .canvas
    width: 100%

  .tabs > .tab-content > .tab-pane
    background-color: inherit
    border: 0
</style>
